/**
 * Auth Config — reads values from the environment config system (@web/config).
 * Environment selection is driven by VITE_ENV at build time.
 * Set via --mode flag: `vite build --mode dev` → VITE_ENV=dev
 */

import { environment } from '@web/config';

export interface AuthConfig {
  apiProxyUrl: string;
  ssoApiProxyUrl: string;
  ohidApiProxyUrl: string;
  bulkTinRUrl: string;

  loginUrl: string;
  callbackUrl: string;
  refreshUrl: string;
  logoutUrl: string;
  fetchUserDetailsUrl: string;

  redirectUri: string;
  ssoRedirectUri: string;
  ssoRedirectLogin: string;
  logoutRedirectUrl: string;
  providerLoginPage: string;
  clientId: string;
  scopes: string;

  postLoginProxyUrl: string;
  XSRFToggle: boolean;

  isInternal: boolean;
  isInternalSsoLogin: boolean;
  cookieEnvironment: string;
  upstreamEnv: string;
  domain: string;
  toggleNewLoginApiChanges: boolean;
  tinLevelExternalChange: boolean;

  externalTinsJOB?: string;
  goldCardApi?: string;
}

/** Values that are constant across all environments */
const AUTH_CONSTANTS = {
  clientId: 'PED42235',
  scopes: 'openid profile',
  externalTinsJOB: 'provider-org-jobs/submit-external-tins-job',
};

function buildConfig(): AuthConfig {
  const isInternal = environment.internalAccess;
  return {
    apiProxyUrl: environment.apiProxyUrl,
    ssoApiProxyUrl: environment.ssoApiProxyUrl,
    ohidApiProxyUrl: environment.ohidApiProxyUrl ?? '',
    bulkTinRUrl: environment.apiUrls.bulkTinRUrl,
    loginUrl: isInternal ? 'api/auth/login' : 'api/auth/ohid/login',
    callbackUrl: isInternal ? 'api/auth/callback' : 'api/auth/ohid/callback',
    refreshUrl: isInternal ? 'api/auth/refresh' : 'api/auth/ohid/refresh',
    logoutUrl: isInternal ? 'api/auth/logout' : 'api/auth/ohid/logout',
    fetchUserDetailsUrl: 'api/auth/fetchUserDetails',
    redirectUri: isInternal ? environment.redirectUri : ((environment as any).ohidredirectUri ?? environment.redirectUri),
    ssoRedirectUri: environment.apiUrls.SsoRedirectUri ?? '',
    ssoRedirectLogin: environment.apiUrls.SsoRedirectLogin ?? '',
    logoutRedirectUrl: environment.apiUrls.SsoLogoutUrl ?? '',
    providerLoginPage: environment.apiUrls.providerLoginPage ?? '',
    clientId: AUTH_CONSTANTS.clientId,
    scopes: AUTH_CONSTANTS.scopes,
    postLoginProxyUrl: environment.postLoginProxyUrl,
    XSRFToggle: environment.XSRFToggle,
    isInternal,
    isInternalSsoLogin: environment.internalSsoLogin,
    cookieEnvironment: environment.cookieEnvironment,
    upstreamEnv: environment.upstreamEnv,
    domain: environment.domain,
    toggleNewLoginApiChanges: environment.toggleNewLoginApiChanges,
    tinLevelExternalChange: environment.tinLevelExternalChange,
    externalTinsJOB: AUTH_CONSTANTS.externalTinsJOB,
    goldCardApi: environment.goldCardApi,
  };
}

export function getAuthConfig(): AuthConfig {
  return buildConfig();
}

export function waitForAuthConfig(): Promise<AuthConfig> {
  return Promise.resolve(buildConfig());
}
